import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import type React from 'react';
import { cn } from '../../features/shared/utils/cn';
import { useConfig } from '../components/Config';
import { isRelativeUrl } from '../components/Link/isRelativeUrl';

const buttonVariants = cva(
	cn(
		'inline-flex items-center justify-between gap-3 rounded-[4px]',
		'disabled:pointer-events-none',
		'py-3 px-4',
		'text-[16px] mobile-xl:text-[18px]',
		'transition-[color,background-color,border-color,box-shadow] duration-300',
	),
	{
		variants: {
			variant: {
				primary: cn(
					// Default design
					'bg-primary-default text-white shadow-[0px_-3px_0px_0px_#005b99_inset]',
					// Hover design
					'hover:bg-[#005a98] hover:shadow-none',
					// Focus outline
					'focus-visible:[outline:2px_solid_#430042] focus-visible:shadow-none',
				),
				secondary: cn(
					'text-white border-2 border-white',
					// Hover design
					'hover:bg-white hover:text-black',
					// Focus design
					'focus-visible:bg-white focus-visible:text-black',
					// Focus outline
					'focus-visible:[outline:2px_solid_#430042] focus-visible:-outline-offset-[4px]',
				),
			},
		},
		defaultVariants: {
			variant: 'primary',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	icon?: React.ReactNode;
	href?: string;
	ref?: React.Ref<HTMLButtonElement>;
}

export const Button = ({
	className,
	children,
	variant,
	icon,
	href,
	ref,
	...props
}: ButtonProps) => {
	const { translations } = useConfig();
	const style = buttonVariants({ variant, className });
	if (href) {
		// For hash-only links, use a plain anchor to ensure hashchange events work properly
		if (href.startsWith('#')) {
			return (
				<a href={href} className={style}>
					{children}
					{icon}
				</a>
			);
		}
		return (
			<Link href={href} legacyBehavior>
				<a
					href={href}
					className={style}
					target={isRelativeUrl(href) ? undefined : '_blank'}
					rel="noreferrer"
				>
					{children}
					{!isRelativeUrl(href) && (
						<span className="sr-only">{translations.externalLinkLabel}</span>
					)}
					{icon}
				</a>
			</Link>
		);
	}
	return (
		<button type="button" className={style} {...props}>
			{children}
			{icon}
		</button>
	);
};

Button.displayName = 'Button';
